import { useEffect, useState } from "react";
import { swr, invalidate } from "../outbox.js";
import { api, type Person, type Team, type Spell } from "../api.js";
import { useNavigate } from "react-router-dom";
import { IconPlus, IconChart, IconChevron } from "../Icons.js";
import { Screen, Card, Row, Button, Field, Empty, Msg, Skeleton } from "../ui.js";

export function People({ clubId, canEdit }: { clubId: string; canEdit: boolean }) {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [open, setOpen] = useState<Person | null>(null);
  const nav = useNavigate();

  const load = async () => {
    const paint = (d: { people: Person[]; teams: Team[] }) => {
      setPeople(d.people); setTeams(d.teams);
    };
    // Cache first, network second. Switching tabs used to refetch the whole
    // club every time — six times in one session.
    const hit = await swr(`club:${clubId}`,
      () => api<{ people: Person[]; teams: Team[] }>(`/api/v1/clubs/${clubId}`), paint);
    if (hit) paint(hit); else if (!navigator.onLine) setMsg("Offline — showing nothing yet.");
  };
  useEffect(() => { void load(); }, [clubId]);

  const add = async () => {
    const n = name.trim();
    if (!n) return setMsg("A player needs a name.");
    const r = await api<{ error?: string }>(`/api/v1/clubs/${clubId}/people`, { json: { displayName: n } });
    if (r.ok) { setName(""); setAdding(false); setMsg(null); invalidate(`club:${clubId}`); void load(); }
    else setMsg(r.data.error ?? "Could not add them.");
  };

  return (
    <Screen title="People" sub={people ? `${people.length} in the club` : undefined}
      action={canEdit ? <Button onClick={() => setAdding((v) => !v)}>
            <span className="flex items-center gap-1.5">
              {!adding && <IconPlus size={15} />}{adding ? "Cancel" : "Add"}
            </span>
          </Button> : undefined}>
      <Msg text={msg} kind="err" />

      {adding && (
        <div className="mb-4 space-y-3 rounded-xl border border-line bg-panel p-4">
          <Field label="Name" value={name} autoFocus placeholder="e.g. Zeeshan"
                 onChange={(e) => setName(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && void add()} />
          <Button full onClick={add}>Add to the club</Button>
        </div>
      )}

      {!people ? <Card><Skeleton rows={4} /></Card>
      : people.length === 0 ? <Card><Empty title="Nobody yet">Add your squad to get started.</Empty></Card>
      : (
        <Card>
          {people.map((p) => (
            <Row key={p.personId} onClick={() => setOpen(p)}>
              <div className="min-w-0 flex-1">
                <b className="block truncate font-semibold">{p.displayName}</b>
              </div>
              {p.playerId && (
                <span onClick={(e) => { e.stopPropagation(); nav(`/player/${p.playerId}`); }}
                  aria-label="Career statistics"
                  className="flex h-11 w-11 items-center justify-center rounded-full border
                             border-line text-faint hover:border-amber hover:text-amber">
                  <IconChart size={16} />
                </span>
              )}
              <span className="text-faint"><IconChevron /></span>
            </Row>
          ))}
        </Card>
      )}

      {open && (
        <PersonSheet person={open} teams={teams} canEdit={canEdit}
          onClose={() => setOpen(null)} onChanged={() => { setOpen(null); invalidate(`club:${clubId}`); void load(); }} />
      )}
    </Screen>
  );
}

function PersonSheet({ person, teams, canEdit, onClose, onChanged }: {
  person: Person; teams: Team[]; canEdit: boolean;
  onClose: () => void; onChanged: () => void;
}) {
  const [spells, setSpells] = useState<Spell[]>([]);
  const [name, setName] = useState(person.displayName);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!person.playerId) return;
    void api<{ spells: Spell[] }>(`/api/v1/players/${person.playerId}/history`)
      .then((r) => r.ok && setSpells(r.data.spells));
  }, [person.playerId]);

  const currentTeam = spells.find((s) => s.toDate === null);

  const save = async () => {
    const r = await api<{ error?: string }>(`/api/v1/people/${person.personId}`,
      { method: "PATCH", json: { displayName: name.trim() } });
    r.ok ? onChanged() : setMsg(r.data.error ?? "Could not save.");
  };

  const move = async (toTeamId: string | null) => {
    if (!person.playerId) return;
    const r = await api<{ error?: string }>(`/api/v1/players/${person.playerId}/transfer`,
      { json: { toTeamId } });
    r.ok ? onChanged() : setMsg(r.data.error ?? "Could not transfer.");
  };

  const archive = async () => {
    if (!confirm(`Archive ${person.displayName}? Their past matches stay on record.`)) return;
    const r = await api<{ error?: string }>(`/api/v1/people/${person.personId}`, { method: "DELETE" });
    r.ok ? onChanged() : setMsg(r.data.error ?? "Could not archive.");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 split:items-center split:p-6"
         onClick={onClose}>
      <div className="max-h-[88vh] w-full max-w-[520px] overflow-y-auto rounded-t-2xl border border-line
                      bg-panel p-5 pb-[calc(20px+var(--sab))] split:rounded-2xl"
           onClick={(e) => e.stopPropagation()}>
        <Msg text={msg} kind="err" />
        <h2 className="mb-4 font-num text-head font-semibold">{person.displayName}</h2>

        {canEdit && (
          <div className="mb-5 space-y-3">
            <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} />
            <Button full kind="ghost" onClick={save}>Save</Button>
          </div>
        )}

        <h3 className="mb-2 font-mono text-label uppercase tracking-[.14em] text-faint">
          Team history
        </h3>
        <div className="mb-5 rounded-card border border-line">
          {spells.length === 0 ? (
            <p className="px-3.5 py-3 text-body text-faint">Not in a team yet.</p>
          ) : spells.map((s, i) => (
            <div key={i} className="flex items-center justify-between border-b border-line px-3.5 py-2.5 last:border-b-0">
              <span className="text-body">{s.name}</span>
              <span className="font-mono text-meta text-faint">
                {s.fromDate} → {s.toDate ?? "present"}
              </span>
            </div>
          ))}
        </div>

        {canEdit && person.playerId && (
          <>
            <h3 className="mb-2 font-mono text-label uppercase tracking-[.14em] text-faint">
              {currentTeam ? "Transfer to" : "Add to a team"}
            </h3>
            <div className="mb-5 grid grid-cols-2 gap-2">
              {teams.filter((t) => t.id !== currentTeam?.teamId).map((t) => (
                <Button key={t.id} kind="ghost" onClick={() => move(t.id)}>{t.shortName} — {t.name}</Button>
              ))}
              {currentTeam && <Button kind="ghost" onClick={() => move(null)}>Leave {currentTeam.shortName}</Button>}
            </div>
            <Button full kind="danger" onClick={archive}>Archive this player</Button>
          </>
        )}
        <div className="mt-2"><Button full kind="ghost" onClick={onClose}>Close</Button></div>
      </div>
    </div>
  );
}
